import { useAuth } from '../auth/useAuth';
import { Brand, Icon } from './ui.jsx';

/* ---------- Navigation principale ----------
   Liens en <a href> volontairement : l'app legacy se route sur
   window.location.pathname et ne se re-rend que sur popstate, donc une
   navigation client (pushState) ne rafraîchirait pas la page.

   Ce header est le seul du site : les pages legacy (app.jsx) et les pages
   React sous FrontofficeLayout l'utilisent toutes les deux, pour que la
   navigation ne change pas d'une page à l'autre. */
const NAV_ITEMS = [
  { href: '/', label: 'Accueil', isCurrent: (path) => path === '/' },
  { href: '/parcours', label: 'Les parcours', isCurrent: (path) => path.startsWith('/parcours') },
  { href: '/search', label: 'Rechercher', icon: 'search', isCurrent: (path) => path.startsWith('/search') }
];

/* ---------- Navbar / Header ---------- */
export const Navbar = ({ transparent, current }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated, isAdmin, isEditor, logout } = useAuth();

  const path = current || window.location.pathname || '/';
  const canReachAdmin = isAuthenticated && (isAdmin || isEditor);
  // Le fond translucide n'est transparent qu'en haut de la landing, menu fermé.
  const solid = scrolled || !transparent || menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Le menu mobile se ferme à l'échap et dès qu'on repasse en largeur desktop.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onResize = () => { if (window.innerWidth > 980) setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [menuOpen]);

  const navItems = NAV_ITEMS.map((item) => {
    const isCurrent = item.isCurrent(path);
    return (
      <a
        key={item.href}
        href={item.href}
        className={'nav-pill' + (isCurrent ? ' is-current' : '')}
        aria-current={isCurrent ? 'page' : undefined}
      >
        {item.icon && <Icon name={item.icon} size={13} />}
        {item.label}
      </a>
    );
  });

  const authActions = (
    <>
      {canReachAdmin && (
        <a href="/admin" className="nav-action">
          <Icon name="settings" size={13} /> Administration
        </a>
      )}
      {isAuthenticated
        ? (
          <button type="button" onClick={logout} className="nav-action">
            <Icon name="logout" size={13} /> Déconnexion
          </button>
        )
        : <a href="/signin" className="nav-action">Connexion</a>}
    </>
  );

  return (
    <header
      className="site-header"
      style={{
        background: solid ? 'rgba(243,239,230,0.97)' : 'transparent',
        backdropFilter: solid ? 'blur(12px) saturate(140%)' : 'none',
        borderBottomColor: solid && scrolled ? 'var(--ink)' : 'transparent'
      }}
    >
      <div className="container-w row" style={{ padding: scrolled ? '10px 40px' : '18px 40px', gap: 28, transition: 'padding .25s' }}>
        <Brand small={scrolled} />

        {/* Navigation — masquée sous 980px, remplacée par le menu burger */}
        <nav className="nav-pills hide-mobile" style={{ margin: '0 auto' }} aria-label="Navigation principale">
          {navItems}
        </nav>

        <div className="row gap-5 hide-mobile">
          {authActions}
          <a href="/publier" className="btn btn-primary">Publier votre parcours</a>
        </div>

        <button
          type="button"
          className="nav-burger show-mobile"
          style={{ marginLeft: 'auto' }}
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-controls="nav-mobile"
          aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          <Icon name={menuOpen ? 'close' : 'menu'} size={20} />
        </button>
      </div>

      {menuOpen && (
        <nav id="nav-mobile" className="nav-mobile show-mobile" aria-label="Navigation principale">
          <div className="nav-mobile__list">{navItems}</div>
          <div className="nav-mobile__actions">
            {authActions}
            <a href="/publier" className="btn btn-primary" style={{ justifyContent: 'center' }}>
              Publier votre parcours
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};
